import { useState } from 'react';
import { X } from 'lucide-react';
import { coachPersonas } from '../lib/personas';

interface CombinedCoachProfile {
  id: string;
  name: string;
  descriptor: string;
  frameworks: string;
  tone: string;
  activeCoaches: string[];
}

interface CombinedCoachProfilePopoverProps {
  combinedCoach: CombinedCoachProfile;
  children: React.ReactNode;
}

const CombinedCoachProfilePopover = ({ combinedCoach, children }: CombinedCoachProfilePopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setIsOpen(!isOpen);
  };

  // Get individual coach data for active coaches
  const activeCoachData = combinedCoach.activeCoaches.map(coachId =>
    coachPersonas.find(coach => coach.id === coachId)
  ).filter(Boolean);

  // Extract specialties from frameworks
  const specialties = combinedCoach.frameworks
    .split('→')
    .map(s => s.trim())
    .filter(s => s.length > 0);

  return (
    <>
      <div
        className="relative inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {children}
      </div>

      {/* Popover */}
      {isOpen && (
        <>
          {/* Mobile Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setIsOpen(false)}
          />

          {/* Popover Content */}
          <div
            className="fixed z-50 w-80 max-w-[90vw]"
            style={{
              left: `${Math.min(Math.max(position.x - 160, 16), window.innerWidth - 336)}px`,
              top: `${Math.max(position.y - 20, 16)}px`,
              transform: 'translateY(-100%)'
            }}
          >
            {/* Arrow */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div className="w-3 h-3 bg-white border-r border-b border-alto-grey transform rotate-45"></div>
            </div>

            {/* Content Card */}
            <div className="bg-white rounded-2xl border border-alto-grey shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-alto-grey">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-lg font-bold text-gray-900 tracking-tight">
                      {combinedCoach.name}
                    </h3>
                    <p className="text-sm font-display font-medium text-royal-blue uppercase tracking-wider">
                      Combined Coach Perspective
                    </p>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/60 text-gray-500 hover:text-gray-700 transition-colors md:hidden"
                    aria-label="Close profile"
                  >
                    <X className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {/* Bio Section */}
              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-display text-sm font-semibold text-gray-900 mb-2">
                      About This Combination
                    </h4>
                    <p className="font-body text-sm text-gray-700 leading-relaxed">
                      {combinedCoach.descriptor}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-display text-sm font-semibold text-gray-900 mb-2">
                      Active Coaches
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {activeCoachData.map((coach) => (
                        <div key={coach!.id} className="flex items-center gap-2 bg-gray-50 rounded-full px-3 py-1">
                          <div className="w-4 h-4 rounded-full bg-gradient-to-r" style={{
                            background: `linear-gradient(135deg, ${coach!.color.split(' ')[1]}, ${coach!.color.split(' ')[3]})`
                          }}></div>
                          <span className="text-xs font-display font-medium text-gray-700">
                            {coach!.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-display text-sm font-semibold text-gray-900 mb-2">
                      Coaching Style
                    </h4>
                    <p className="font-body text-sm text-gray-700 leading-relaxed">
                      {combinedCoach.tone}
                    </p>
                  </div>

                  {/* Frameworks */}
                  {specialties.length > 0 && (
                    <div>
                      <h4 className="font-display text-sm font-semibold text-gray-900 mb-2">
                        Key Frameworks
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {specialties.map((specialty, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-xs font-display font-medium text-purple-700"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CombinedCoachProfilePopover;
